import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

interface EnhancedKPICardProps {
  title: string;
  period: string;
  value: number | string | null;
  unit?: string;
  currency?: string;
  previousYear?: number | null;
  changePercent?: number | null;
  vsPreCovid?: number | null;
  vsCovidLow?: number | null;
  onClick?: () => void;
  clickable?: boolean;
  icon?: React.ReactNode;
}

export const EnhancedKPICard: React.FC<EnhancedKPICardProps> = ({
  title,
  period,
  value,
  unit,
  currency,
  previousYear,
  changePercent,
  vsPreCovid,
  vsCovidLow,
  onClick,
  clickable = true,
  icon,
}) => {
  const getTrendIcon = (change: number | null | undefined) => {
    if (change === null || change === undefined) return <TrendingFlat sx={{ fontSize: 16 }} />;
    if (change > 1) return <TrendingUp color="success" sx={{ fontSize: 16 }} />;
    if (change < -1) return <TrendingDown color="error" sx={{ fontSize: 16 }} />;
    return <TrendingFlat sx={{ fontSize: 16 }} />;
  };

  const getChangeColor = (change: number | null | undefined) => {
    if (change === null || change === undefined) return 'text.secondary';
    return change >= 0 ? 'success.main' : 'error.main';
  };

  const formatValue = (val: number | string | null) => {
    if (val === null || val === undefined) return '--';
    if (currency && typeof val === 'number') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val);
    }
    if (unit === '%' && typeof val === 'number') {
      return `${val.toFixed(1)}%`;
    }
    if (typeof val === 'number') {
      return val.toLocaleString('en-US', { maximumFractionDigits: 1 });
    }
    return val;
  };

  return (
    <Card
      onClick={clickable && onClick ? onClick : undefined}
      sx={{
        cursor: clickable && onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        height: '100%',
        '&:hover': clickable && onClick
          ? {
              transform: 'scale(1.02)',
              boxShadow: 3,
            }
          : {},
      }}
    >
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            {icon && <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{icon}</Box>}
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
          </Box>
          <Chip
            label={period}
            size="small"
            variant="outlined"
            sx={{ borderRadius: '4px' }}
          />
        </Box>

        {/* Main Value */}
        <Box mb={2}>
          <Typography variant="h3" component="div" fontWeight="bold">
            {formatValue(value)}
            {unit && unit !== '%' && (
              <Typography component="span" variant="h6" color="text.secondary" ml={1}>
                {unit}
              </Typography>
            )}
          </Typography>

          {/* Previous Year Comparison and YoY Change on same line */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
            {previousYear !== null && previousYear !== undefined && (
              <Typography variant="body2" color="text.secondary">
                Last Year: {formatValue(previousYear)}
              </Typography>
            )}

            {changePercent !== null && changePercent !== undefined && (
              <Box display="flex" alignItems="center" gap={0.5}>
                {getTrendIcon(changePercent)}
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  color={getChangeColor(changePercent)}
                >
                  {changePercent > 0 ? '+' : ''}
                  {changePercent.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  YoY
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* COVID Comparisons */}
        {(vsPreCovid !== null || vsCovidLow !== null) && (
          <Box
            mt={2}
            pt={2}
            borderTop={1}
            borderColor="divider"
          >
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              COVID Recovery
            </Typography>

            <Box display="flex" flexDirection="column" gap={0.5}>
              {vsPreCovid !== null && vsPreCovid !== undefined && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getTrendIcon(vsPreCovid)}
                  <Typography variant="body2" color={getChangeColor(vsPreCovid)}>
                    {vsPreCovid > 0 ? '+' : ''}{vsPreCovid.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    vs Pre-COVID
                  </Typography>
                </Box>
              )}

              {vsCovidLow !== null && vsCovidLow !== undefined && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getTrendIcon(vsCovidLow)}
                  <Typography variant="body2" color={getChangeColor(vsCovidLow)}>
                    {vsCovidLow > 0 ? '+' : ''}{vsCovidLow.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    vs COVID Low
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};